import React, { useState } from 'react';
import { X, Calculator } from 'lucide-react';
import { deliveryFeeService } from '../../services/deliveryFeeService';
import { DeliveryFeeConfig, DeliveryFeeCalculationResponse } from '../../types/deliveryFee';
import { useToast } from '../../hooks/use-toast';

interface Props {
    configs: DeliveryFeeConfig[];
    onClose: () => void;
}

const DeliveryFeeCalculator: React.FC<Props> = ({ configs, onClose }) => {
    const { toast } = useToast();
    const [calculating, setCalculating] = useState(false);
    const [result, setResult] = useState<DeliveryFeeCalculationResponse | null>(null);
    const [formData, setFormData] = useState({
        config_id: configs.find(c => c.is_default)?.id || configs[0]?.id || undefined,
        distance_km: '',
        total_weight_kg: '',
        total_quantity: '',
        order_subtotal: ''
    });

    const handleCalculate = async () => {
        setCalculating(true);
        setResult(null);

        try {
            const response = await deliveryFeeService.calculate({
                config_id: formData.config_id,
                distance_km: formData.distance_km ? parseFloat(formData.distance_km) : undefined,
                total_weight_kg: formData.total_weight_kg ? parseFloat(formData.total_weight_kg) : undefined,
                total_quantity: formData.total_quantity ? parseFloat(formData.total_quantity) : undefined,
                order_subtotal: formData.order_subtotal ? parseFloat(formData.order_subtotal) : undefined
            });
            setResult(response);
        } catch (error: any) {
            toast({
                title: 'Erreur',
                description: error.response?.data?.detail || 'Erreur lors du calcul',
                variant: 'destructive'
            });
        } finally {
            setCalculating(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-900 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 px-6 py-4 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <Calculator className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100">Calculateur de Frais</h2>
                    </div>
                    <button onClick={onClose} className="text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Config Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                            Configuration
                        </label>
                        <select
                            value={formData.config_id}
                            onChange={(e) => setFormData({ ...formData, config_id: parseInt(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            {configs.map((config) => (
                                <option key={config.id} value={config.id}>
                                    {config.name} {config.is_default ? '(Par défaut)' : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Input Fields */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                                Distance (km)
                            </label>
                            <input
                                type="number"
                                step="0.1"
                                value={formData.distance_km}
                                onChange={(e) => setFormData({ ...formData, distance_km: e.target.value })}
                                placeholder="Ex: 25.5"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                                Poids Total (kg)
                            </label>
                            <input
                                type="number"
                                step="0.1"
                                value={formData.total_weight_kg}
                                onChange={(e) => setFormData({ ...formData, total_weight_kg: e.target.value })}
                                placeholder="Ex: 12.5"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                                Quantité d'Articles
                            </label>
                            <input
                                type="number"
                                step="1"
                                value={formData.total_quantity}
                                onChange={(e) => setFormData({ ...formData, total_quantity: e.target.value })}
                                placeholder="Ex: 8"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                                Montant Commande (€)
                            </label>
                            <input
                                type="number"
                                step="0.01"
                                value={formData.order_subtotal}
                                onChange={(e) => setFormData({ ...formData, order_subtotal: e.target.value })}
                                placeholder="Ex: 150.00"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    {/* Calculate Button */}
                    <button
                        onClick={handleCalculate}
                        disabled={calculating}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                        <Calculator className="w-4 h-4" />
                        {calculating ? 'Calcul en cours...' : 'Calculer les Frais'}
                    </button>

                    {/* Result */}
                    {result && (
                        <div className={`border rounded-lg p-4 ${result.is_free_delivery ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' : 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800'
                            }`}>
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-sm font-medium text-gray-700 dark:text-slate-300">Frais de Livraison</span>
                                <span className={`text-2xl font-bold ${result.is_free_delivery ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'
                                    }`}>
                                    {result.is_free_delivery ? 'GRATUIT' : `${result.delivery_fee.toFixed(2)}€`}
                                </span>
                            </div>

                            {!result.is_free_delivery && result.breakdown.calculation && (
                                <div className="text-sm text-gray-600 dark:text-slate-400 mb-3">
                                    <p className="font-medium mb-1">Détail du calcul :</p>
                                    <p className="bg-white dark:bg-slate-800 p-2 rounded border border-gray-200 dark:border-slate-700">
                                        {result.breakdown.calculation}
                                    </p>
                                </div>
                            )}

                            <div className="text-sm space-y-1">
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-slate-400">Configuration:</span>
                                    <span className="font-medium text-gray-900 dark:text-slate-100">{result.config_name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-slate-400">Méthode:</span>
                                    <span className="font-medium text-gray-900 dark:text-slate-100">{result.calculation_method}</span>
                                </div>
                            </div>

                            {result.is_free_delivery && result.breakdown.message && (
                                <div className="mt-3 p-2 bg-green-100 dark:bg-green-950/30 rounded text-sm text-green-800 dark:text-green-300">
                                    {result.breakdown.message}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Info */}
                    <div className="bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4 text-sm text-gray-700 dark:text-slate-300">
                        <p className="font-medium mb-1">💡 Astuce</p>
                        <p>
                            Remplissez les champs selon la méthode de calcul de la configuration sélectionnée.
                            Vous pouvez laisser certains champs vides s'ils ne sont pas nécessaires.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeliveryFeeCalculator;
